import {
  type ImageDimensions,
  imageDimensions,
} from "../../shared/imageDimensions";

const JPEG_QUALITIES = [0.8, 0.6, 0.4];
const HEADER_BYTES = 1024 * 1024;

export type ProcessedImage = {
  blob: Blob;
  mediaType: string;
  width: number;
  height: number;
};

function encode(
  canvas: HTMLCanvasElement,
  mediaType: string,
  quality?: number
) {
  return new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, mediaType, quality)
  );
}

async function readDimensions(file: Blob) {
  const header = new Uint8Array(
    await file.slice(0, HEADER_BYTES).arrayBuffer()
  );
  return imageDimensions(header);
}

/**
 * Decodes at the smallest size that still satisfies `edge`, so a full-resolution
 * bitmap is never allocated. Only one axis is constrained, so an EXIF rotation
 * that transposes the image keeps its aspect ratio.
 */
function decode(
  file: Blob,
  source: ImageDimensions,
  edge: { pixels: number; constrain: "longer" | "shorter" }
) {
  const target =
    edge.constrain === "longer"
      ? Math.max(source.width, source.height)
      : Math.min(source.width, source.height);
  if (target <= edge.pixels) {
    return createImageBitmap(file, { imageOrientation: "from-image" });
  }
  const constrainWidth =
    edge.constrain === "longer"
      ? source.width >= source.height
      : source.width <= source.height;
  return createImageBitmap(file, {
    imageOrientation: "from-image",
    resizeQuality: "high",
    ...(constrainWidth
      ? { resizeWidth: edge.pixels }
      : { resizeHeight: edge.pixels }),
  });
}

function canvasOf(width: number, height: number) {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");
  return { canvas, ctx };
}

async function encodeWithinBudget(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  { maxBytes, sourceMediaType }: { maxBytes: number; sourceMediaType: string }
): Promise<ProcessedImage | null> {
  const size = { width: canvas.width, height: canvas.height };
  // Re-encoding as JPEG puts ringing around screenshot text, so stay lossless
  // for lossless sources; WebP becomes PNG because WebKit cannot encode WebP.
  const preferred =
    sourceMediaType === "image/jpeg"
      ? { mediaType: "image/jpeg", quality: 0.92 }
      : { mediaType: "image/png", quality: undefined };
  const first = await encode(canvas, preferred.mediaType, preferred.quality);
  if (first && first.size <= maxBytes) {
    return { blob: first, mediaType: first.type, ...size };
  }

  // JPEG carries no alpha, so composite onto white before falling back to lossy.
  ctx.globalCompositeOperation = "destination-over";
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  let smallest = first;
  for (const quality of JPEG_QUALITIES) {
    const jpeg = await encode(canvas, "image/jpeg", quality);
    if (!jpeg) continue;
    if (jpeg.size <= maxBytes) {
      return { blob: jpeg, mediaType: jpeg.type, ...size };
    }
    if (!smallest || jpeg.size < smallest.size) smallest = jpeg;
  }
  return smallest
    ? { blob: smallest, mediaType: smallest.type, ...size }
    : null;
}

/**
 * Shrinks an image to fit `maxDimension` on its longest edge and `maxBytes` on
 * the wire. Returns null when the original already fits or its dimensions
 * cannot be read.
 */
export async function fitImage(
  file: Blob,
  {
    maxDimension,
    maxBytes,
    mediaType = file.type,
  }: { maxDimension: number; maxBytes: number; mediaType?: string }
): Promise<ProcessedImage | null> {
  const source = await readDimensions(file);
  if (!source) return null;
  if (
    source.width <= maxDimension &&
    source.height <= maxDimension &&
    file.size <= maxBytes
  ) {
    return null;
  }

  const bitmap = await decode(file, source, {
    pixels: maxDimension,
    constrain: "longer",
  });
  try {
    const scale = Math.min(
      1,
      maxDimension / bitmap.width,
      maxDimension / bitmap.height
    );
    const { canvas, ctx } = canvasOf(
      bitmap.width * scale,
      bitmap.height * scale
    );
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return await encodeWithinBudget(canvas, ctx, {
      maxBytes,
      sourceMediaType: mediaType,
    });
  } finally {
    bitmap.close();
  }
}

/** Center-crops to a `size` × `size` square, scaling to cover. */
export async function cropImageToSquare(
  file: Blob,
  {
    size,
    mediaType,
    quality,
  }: { size: number; mediaType: string; quality: number }
): Promise<ProcessedImage> {
  const source = await readDimensions(file);
  const bitmap = source
    ? await decode(file, source, { pixels: size, constrain: "shorter" })
    : await createImageBitmap(file, { imageOrientation: "from-image" });
  try {
    const { canvas, ctx } = canvasOf(size, size);
    const scale = Math.max(size / bitmap.width, size / bitmap.height);
    const width = bitmap.width * scale;
    const height = bitmap.height * scale;
    ctx.drawImage(
      bitmap,
      (size - width) / 2,
      (size - height) / 2,
      width,
      height
    );
    const blob = await encode(canvas, mediaType, quality);
    if (!blob) throw new Error("Failed to encode image");
    return { blob, mediaType: blob.type, width: size, height: size };
  } finally {
    bitmap.close();
  }
}
